import React from "react";

import { getRecommendationMode } from "QuotePage/app/compactQuotePage/Selectors";
import { getPlanDeviationProps, getPlanFromPathId, hasPlansBeforeInitialData, getVisiblePlans, getPayrollLead } from "QuotePage/Selectors";
import { connect } from "react-redux";
import { flatten } from "underscore";
import formatCurrency from "Utils/formatCurrency";

import Button, { LinkButton } from "Cobalt/Button";
import { Collapse } from "Cobalt/Collapse";
import { ModalHeader, openModal } from "Cobalt/Modal";
import { FooterTD, FooterTR, HeaderTR, Table, TD, TH, TR } from "Cobalt/TableV2";
import { CarrierLogo } from "Common/CarrierLogo";
import { addPlanToCart } from "Common/CartService";
import FinePrint from "Common/FinePrint";
import { Row, Col } from "Common/Grid";
import Icon from "Common/Icon";

import makeBem from "Utils/makeBem";

const bem = makeBem("MobilePlanDetails");

class MobilePlanDetails extends React.Component {
  render() {
    const { plan, employeeProfile, planDeviationProps, onHide, payrollLead } = this.props;

    return (
      <div className={bem()}>
        <ModalHeader bsClass={bem("hidden-header")} key="header" closeButton onHide={onHide} />
        {this.renderPlanHeader(plan)}
        <DetailSection title="Plan Summary">{this.renderPlanSummary(plan, payrollLead)}</DetailSection>
        <DetailSection title="Individual Employee Pricing">
          <SimpleBreakdownTable className="cost-breakdown-table" employeeProfile={employeeProfile} plan={plan} />
        </DetailSection>
        <div className={bem("divider-section")}>Plan Details</div>
        <FinePrint plan={plan} disableTitle isMobile planDeviationProps={planDeviationProps} />
      </div>
    );
  }

  renderPlanHeader(plan) {
    return (
      <div className="plan-identifier">
        <Row className="title-row">
          <Col xs={6} pl={5}>
            <CarrierLogo plan={plan} maxSize={5} />
          </Col>
        </Row>
        <div className="plan-title">
          <div className="plan-name" title={plan.raw_name}>
            {plan.display_name}
          </div>
        </div>
      </div>
    );
  }

  renderMovileStartApplicationButton(plan, payrollLead) {
    if (payrollLead?.partner) {
      return (
        <LinkButton size="medium" my={2} fullWidth href={`/${payrollLead.partner}/router/payroll-prospect`}>
          Get Started
        </LinkButton>
      );
    }
    return (
      <Button color="primary" fullWidth href={plan.select_plan_url} onClick={this.handleOnClickAddToCart.bind(this, plan)} disabled={this.props.disableButtons}>
        Get Started
      </Button>
    );
  }

  renderPlanSummary(plan, payrollLead) {
    return (
      <div className="plan-identifier">
        <div className="plan-terms" pl={5}>
          <AttributeRow label="Monthly Cost" value={`${formatCurrency(plan.average_monthly_premium)} / employee`} />

          <AttributeRow label="Network Type" value={plan.plan_type} />
          <AttributeRow label="Deductible" value={formatCurrency(plan.deductible)} />

          <AttributeRow label="Maximum" value={formatCurrency(plan.limit)} />
          <AttributeRow label="Doctor's Visits" value={formatCurrency(plan.estimated_costs.pcp_visit)} />

          <AttributeRow label="Generic Drugs" value={formatCurrency(plan.estimated_costs.generic_drugs)} />

          <AttributeRow label="Hospitalization" value={formatCurrency(plan.estimated_costs.hospitalization)} />
        </div>
        {!this.props.isSampleQuote && !this.props.inCart && this.renderMovileStartApplicationButton(plan, payrollLead)}
      </div>
    );
  }

  handleOnClickAddToCart(plan, event) {
    event.stopPropagation();
    this.props.onAddToCartClicked("medical", plan, {
      position: this.props.planPosition,
      recommendationMode: this.props.recommendationMode,
      location: "mobile_plan_card",
    });
  }
}

class AttributeRow extends React.Component {
  render() {
    return (
      <Row className="attribute-row">
        <Col xs={1} />
        <Col xs={5}>
          <div className="attribute-label">{this.props.label}</div>
        </Col>
        <Col xs={6}>
          <div className="value">{this.props.value}</div>
        </Col>
      </Row>
    );
  }
}

export class DetailSection extends React.Component {
  constructor() {
    super();
    this.state = {};
    this.handleToggleCollapse = this.handleToggleCollapse.bind(this);
  }

  render() {
    return (
      <div className="detail-section">
        <div className="title" onClick={this.handleToggleCollapse}>
          {this.props.title} <Icon icon={this.state.open ? "angle-up" : "angle-down"} />
        </div>
        <Collapse in={this.state.open}>
          <div>{this.props.children}</div>
        </Collapse>
      </div>
    );
  }

  handleToggleCollapse() {
    this.setState({ open: !this.state.open });
  }
}

class SimpleBreakdownTable extends React.Component {
  getTotalForSelf(costs) {
    return costs ? costs.self_cost : 0;
  }

  getTotalForFamily(costs) {
    return costs ? costs.family_cost : 0;
  }

  render() {
    const { className, plan, employeeProfile } = this.props;

    const priceRows = flatten(
      employeeProfile.map((employee, index) => {
        const costs = plan.employee_costs[index];

        const rows = [
          <TR key={`ee-${index}`}>
            <TD>{employee.name}</TD>
            <TD>{employee.age}</TD>
            <TD align="right">{formatCurrency(this.getTotalForSelf(costs))}</TD>
          </TR>,
        ];

        if (costs && costs.family_cost > 0) {
          rows.push(
            <TR key={`dep-${index}`}>
              <TD>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Dependents</TD>
              <TD>&nbsp;</TD>
              <TD align="right">{formatCurrency(this.getTotalForFamily(costs))}</TD>
            </TR>,
          );
        }
        return rows;
      }),
    );

    const plus = (x, y) => x + y;
    const total = plan.employee_costs.map((c) => this.getTotalForSelf(c) + this.getTotalForFamily(c)).reduce(plus);

    return (
      <div className={className}>
        <Table style={{ tableLayout: "fixed" }}>
          <thead>
            <HeaderTR>
              <TH width="170px">Employee Name</TH>
              <TH width="80px">Age</TH>
              <TH align="right" width="80px">
                Total Cost
              </TH>
            </HeaderTR>
          </thead>
          <tbody>{priceRows}</tbody>
          <tfoot>
            <FooterTR>
              <FooterTD>Total Premium</FooterTD>
              <FooterTD>&nbsp;</FooterTD>
              <FooterTD align="right">{formatCurrency(total)}</FooterTD>
            </FooterTR>
          </tfoot>
        </Table>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const plan = ownProps.plan ? ownProps.plan : getPlanFromPathId(state);
  const position = getVisiblePlans(state).findIndex((p) => p.id === plan.id);
  return {
    payrollLead: getPayrollLead(state),
    plan,
    planPosition: position,
    employeeProfile: state.employeeProfile,
    planDeviationProps: getPlanDeviationProps(state),
    disableButtons: hasPlansBeforeInitialData(state),
    recommendationMode: getRecommendationMode(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  handleClickEnterAges: () => {
    dispatch(openModal("enterAgesModal"));
  },
  onAddToCartClicked: (planType, plan, tracking = {}) => {
    dispatch(
      addPlanToCart({
        planType,
        plan,
        tracking,
        shouldClearDentalVision: false,
        continueToCart: true,
        shouldClearLife: false,
      }),
    );
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(MobilePlanDetails);
